import React from 'react';
import styled from '@emotion/styled';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import { Button, DialogActions } from '@mui/material';
import {
  PickersActionBarProps,
  usePickersTranslations,
} from '@mui/x-date-pickers';
import type { ComponentProps } from 'react';

const StyledDatePicker = styled(DateTimePicker)`
  .Mui-error {
    border-color: #e0e0e0;
  }
`;

type DatePickerProps = Omit<
  ComponentProps<typeof DateTimePicker>,
  'value' | 'onChange' | 'shouldDisableDate' | 'error'
> & {
  'value': Date;
  'onChange': (date: Date) => void;
  'shouldDisableDate'?: (date: Date) => boolean;
  'error'?: boolean;
  'data-testid'?: string;
};

/**
 * MUI x-date-picker doesn't have a way to change ok button variant, so have to create own ActionBar component to override it
 * @see https://mui.com/x/react-date-pickers/custom-components/#component
 */
function CustomActionBar(props: PickersActionBarProps) {
  const { onAccept, actions, className } = props;
  const translations = usePickersTranslations();

  if (!actions || actions.length === 0) {
    return null;
  }

  return (
    <DialogActions className={className}>
      <Button variant="contained" onClick={onAccept} disableElevation>
        {translations.okButtonLabel}
      </Button>
    </DialogActions>
  );
}

export const MHMDatePicker: React.FC<DatePickerProps> = (
  props: DatePickerProps,
) => {
  const {
    value,
    onChange,
    shouldDisableDate,
    error,
    slots,
    slotProps,
    ...rest
  } = props;
  return (
    <StyledDatePicker
      value={dayjs(props.value)}
      onChange={(date: Dayjs | null) => {
        if (!date) {
          return;
        }
        props.onChange(date.toDate());
      }}
      shouldDisableDate={(date: Dayjs) => {
        return props.shouldDisableDate
          ? props.shouldDisableDate(date.toDate())
          : false;
      }}
      shouldDisableTime={(date: Dayjs) => {
        return props.shouldDisableDate
          ? props.shouldDisableDate(date.toDate())
          : false;
      }}
      slotProps={{
        textField: {
          error: props.error,
          inputProps: {
            'data-testid': props['data-testid'],
          },
        },
        ...slotProps,
      }}
      slots={{ actionBar: CustomActionBar, ...slots }}
      {...rest}
    />
  );
};
